import { useState, useRef, useEffect, useMemo } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Check } from 'lucide-react';

// Fixed list of 24 banks sorted alphabetically after "BANK"
const BANK_LIST = [
  "PT. BANK BCA SYARIAH",
  "PT. BANK CENTRAL ASIA Tbk.",
  "PT. BANK CIMB NIAGA TBK",
  "PT. BANK CIMB NIAGA TBK - UNIT USAHA SYARIAH",
  "PT. BANK DANAMON INDONESIA Tbk.",
  "PT. BANK DANAMON INDONESIA UNIT USAHA SYARIAH",
  "PT. BANK MANDIRI (PERSERO) TBK",
  "PT. BANK MAYORA",
  "PT. BANK MEGA SYARIAH",
  "PT. BANK MEGA Tbk.",
  "PT. BANK MUAMALAT INDONESIA",
  "PT. BANK NEGARA INDONESIA (PERSERO)",
  "PT. BANK OCBC NISP",
  "PT. BANK PANIN DUBAI SYARIAH",
  "PT. BANK PERMATA,TBK",
  "PT. BANK PERMATA,TBK UNIT USAHA SYARIAH",
  "PT. BANK RAKYAT INDONESIA (PERSERO)",
  "PT. BANK SINARMAS",
  "PT. BANK SINARMAS UNIT USAHA SYARIAH",
  "PT. BANK SULSELBAR",
  "PT. BANK SULSELBAR UNIT USAHA SYARIAH",
  "PT BANK SYARIAH INDONESIA",
  "PT. BANK TABUNGAN NEGARA (PERSERO)",
  "PT. BANK TABUNGAN NEGARA (PERSERO) UNIT USAHA SYARIAH",
];

export { BANK_LIST };

export default function SearchableBankSelect({ value, onChange, label, required = false, id = 'bank' }) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedBank, setSelectedBank] = useState(value || '');
  const dropdownRef = useRef(null);

  // Filter: match search text against the part AFTER "BANK" in the name
  const filteredBanks = useMemo(() => {
    if (!search.trim()) return BANK_LIST;
    const searchLower = search.toLowerCase().trim();
    return BANK_LIST.filter(bank => {
      const bankLower = bank.toLowerCase();
      // Get text after "bank" keyword
      const bankIdx = bankLower.indexOf('bank');
      const afterBank = bankIdx >= 0 ? bankLower.substring(bankIdx + 4).trim() : bankLower;
      // Match against afterBank part
      return afterBank.includes(searchLower) || bankLower.includes(searchLower);
    });
  }, [search]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => { setSelectedBank(value || ''); }, [value]);

  const handleSelect = (bankName) => {
    setSelectedBank(bankName);
    onChange(bankName);
    setSearch('');
    setIsOpen(false);
  };

  return (
    <div className="space-y-2" ref={dropdownRef}>
      {label && <Label htmlFor={id}>{label} {required && <span className="text-red-500">*</span>}</Label>}
      <div className="relative">
        <Input
          id={id}
          type="text"
          value={isOpen ? search : (selectedBank || '')}
          onChange={(e) => { setSearch(e.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          placeholder="Ketik untuk mencari bank..."
          className={!selectedBank && required ? 'border-red-300' : ''}
          data-testid={`bank-select-${id}`}
        />
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
            {filteredBanks.length > 0 ? filteredBanks.map((bank) => (
              <button
                key={bank}
                type="button"
                className={`w-full text-left px-4 py-2.5 hover:bg-red-50 flex items-center justify-between text-sm border-b border-slate-50 ${selectedBank === bank ? 'bg-red-50 font-semibold' : ''}`}
                onClick={() => handleSelect(bank)}
              >
                <span>{bank}</span>
                {selectedBank === bank && <Check className="h-4 w-4 text-red-600" />}
              </button>
            )) : (
              <div className="px-4 py-3 text-sm text-slate-500">Tidak ada bank ditemukan</div>
            )}
          </div>
        )}
      </div>
      {selectedBank && <p className="text-xs text-emerald-600">Terpilih: {selectedBank}</p>}
    </div>
  );
}


